/**
 * Report Formatter Service - Refactored Version
 * 
 * Handles formatting of analysis reports using composable template components.
 * Eliminates code duplication through shared utilities and configurable formatters.
 */

/**
 * Shared report header template
 */
const REPORT_HEADER_TEMPLATE = `{{reportTitle}}
{{reportTitleUnderline}}

Gemeinschaft: {{communityList}}
Abfrage Start: {{queryStart}}
Abfrage Ende: {{queryEnd}}
Datum Analyse: {{analysisDate}}
{{billingCycleInfo}}

================================================================================

## ZUSAMMENFASSUNG

{{summarySection}}

================================================================================

## GEMEINSCHAFTSSTATUS

{{communityStatusList}}

================================================================================

## DETAILLIERTE ERGEBNISSE

{{communityDetails}}

================================================================================`;

/**
 * Error report template
 */
const ERROR_TEMPLATE = `FEHLERBERICHT
=============

Ein Fehler ist während der Vollständigkeitsprüfung aufgetreten:

Fehler: {{errorMessage}}

## Lösungsschritte:
1. Backend-Server läuft
2. API-Endpunkte sind erreichbar
3. Gemeinschafts-ECN ist gültig
4. Netzwerkverbindung funktioniert`;

/**
 * Report type configurations
 */
const REPORT_CONFIGS = {
  completeness: {
    title: 'VOLLSTÄNDIGKEITSBERICHT',
    titleUnderline: '=====================',
    summaryFields: [
      { label: 'Gemeinschaften gesamt', key: 'totalCommunities' },
      { label: 'Gemeinschaften mit Daten', key: 'communitiesWithData' },
      { label: 'Gemeinschaften ohne Daten', key: 'communitiesWithoutData' },
      { label: 'Gemeinschaften mit fehlenden Daten', key: 'communitiesWithMissingData' }
    ]
  },
  sanity: {
    title: 'ENERGIEBILANZ-VALIDIERUNGSBERICHT',
    titleUnderline: '=================================',
    summaryFields: [
      { label: 'Gemeinschaften gesamt', key: 'totalCommunities' },
      { label: 'Gemeinschaften analysiert', key: 'communitiesAnalyzed' },
      { label: 'Gemeinschaften mit Fehlern (L3, LX)', key: 'communitiesWithErrors' },
      { label: 'Gemeinschaften mit Warnungen (L2)', key: 'communitiesWithWarnings' }
    ]
  },
  combined: {
    title: 'KOMBINIERTER ANALYSEBERICHT',
    titleUnderline: '===========================',
    summaryFields: [
      { label: 'Gemeinschaften gesamt', key: 'totalCommunities' },
      { label: 'Gemeinschaften mit Daten', key: 'communitiesWithData' },
      { label: 'Gemeinschaften ohne Daten', key: 'communitiesWithoutData' },
      { label: 'Gemeinschaften analysiert (Energie)', key: 'communitiesAnalyzed' },
      { label: 'Gemeinschaften mit Fehlern (L3, LX)', key: 'communitiesWithErrors' },
      { label: 'Gemeinschaften mit Warnungen (L2, Abweichungen)', key: 'communitiesWithWarnings' },
      { label: 'Gemeinschaften mit fehlenden Daten', key: 'communitiesWithMissingData' }
    ]
  }
};

/**
 * Status evaluation strategies
 */
const STATUS_EVALUATORS = {
  completeness: (community) => {
    if (community.error) return '[FEHLER]';
    if (!community.hasData) return '[Keine Daten]';
    if (community.meteringPointsWithMissingDataCount && community.meteringPointsWithMissingDataCount > 0) {
      return '[Fehlende Daten]';
    }
    if ((community.daysMissingBefore && community.daysMissingBefore > 0) || 
        (community.daysMissingAfter && community.daysMissingAfter > 0)) {
      return '[Fehlende Daten]';
    }
    if (community.completenessPercentage && community.completenessPercentage < 100) {
      return '[Fehlende Daten]';
    }
    return '[OK]';
  },
  
  sanity: (community) => {
    if (community.error) return '[FEHLER]';
    if (!community.hasData) return '[Keine Daten]';
    if (community.warningDueToBeforeOct2022) return '[Warnung: Vor Okt 2022]';
    if (community.daysWithL3 && community.daysWithL3.length > 0) return '[L3-Werte]';
    if (community.daysWithL2 && community.daysWithL2.length > 0) return '[L2-Werte]';
    if (community.daysWithNotL1 && community.daysWithNotL1.length > 0) return '[Unbekannte Klassifikation]';
    if (community.daysWithNotZero && community.daysWithNotZero.length > 0) return '[Abweichungen]';
    return '[OK]';
  },
  
  combined: (completenessData, sanityData) => {
    if (completenessData.error || sanityData.error) return '[FEHLER]';
    if (!completenessData.hasData || !sanityData.hasData) return '[Keine Daten]';
    if (sanityData.daysWithL3 && sanityData.daysWithL3.length > 0) return '[L3-Werte]';
    if (sanityData.daysWithNotL1 && sanityData.daysWithNotL1.length > 0) return '[Unbekannte Klassifikation]';
    if (completenessData.meteringPointsWithMissingDataCount && completenessData.meteringPointsWithMissingDataCount > 0) {
      return '[Fehlende Daten]';
    }
    if (completenessData.completenessPercentage && completenessData.completenessPercentage < 100) {
      return '[Fehlende Daten]';
    }
    if (sanityData.warningDueToBeforeOct2022) return '[Warnung: Vor Okt 2022]';
    if (sanityData.daysWithL2 && sanityData.daysWithL2.length > 0) return '[L2-Werte]';
    if (sanityData.daysWithNotZero && sanityData.daysWithNotZero.length > 0) return '[Abweichungen]';
    return '[OK]';
  }
};

/**
 * Replace placeholders in template with actual values
 * @param {string} template - Template string with {{placeholder}} syntax
 * @param {Object} data - Data object for replacements
 * @returns {string} Processed template
 */
function replacePlaceholders(template, data) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => 
    data[key] !== undefined ? data[key] : match
  );
}

/**
 * Format date as DD.MM.YYYY
 * @param {Date} date - Date to format
 * @returns {string} Formatted date or empty string
 */
function formatDate(date) {
  if (!date || !(date instanceof Date)) return '';
  
  return [
    date.getDate().toString().padStart(2, '0'),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getFullYear()
  ].join('.');
}

/**
 * Format a list of items with dashes
 * @param {Array} items - Items to format
 * @param {Function} mapper - Optional function to extract value from item
 * @returns {string} Formatted list
 */
function formatList(items, mapper = (item) => item) {
  if (!items || items.length === 0) return '';
  return items.map(item => `-- ${mapper(item)}`).join('\n');
}

/**
 * Generate summary section based on configuration
 * @param {Object} config - Report configuration
 * @param {Object} data - Summary data
 * @returns {string} Formatted summary section
 */
function generateSummarySection(config, data) {
  return config.summaryFields
    .map(field => `• ${field.label}: ${data[field.key] || 0}`)
    .join('\n');
}

/**
 * Generate community status list
 * @param {Array} communities - Community data
 * @param {Function} statusEvaluator - Status evaluation function
 * @param {Object} [sanityData] - Optional sanity data for combined reports
 * @returns {string} Formatted status list
 */
function generateCommunityStatusList(communities, statusEvaluator, sanityData = null) {
  if (!communities || communities.length === 0) return '';
  
  return communities.map(community => {
    const status = sanityData 
      ? statusEvaluator(community, sanityData.find(s => s.ecn === community.ecn) || {})
      : statusEvaluator(community);
    return `${community.ecn} - ${community.name} - ${status}`;
  }).join('\n');
}

/**
 * Get data quality status for combined report community status section
 * @param {Object} sanityData - Sanity analysis data
 * @returns {string} Formatted status based on data quality
 */
function getDataQualityStatus(sanityData) {
  if (!sanityData || sanityData.error || !sanityData.hasData) {
    return '[Keine Daten]';
  }
  
  const hasL2 = sanityData.daysWithL2 && sanityData.daysWithL2.length > 0;
  const hasL3 = sanityData.daysWithL3 && sanityData.daysWithL3.length > 0;
  const hasUndefined = sanityData.daysWithNotL1 && sanityData.daysWithNotL1.length > 0;
  
  let status = [];
  
  if (!hasL2 && !hasL3 && !hasUndefined) {
    status.push('CLEAN');
  } else {
    if (hasL2) status.push('L2');
    if (hasL3) status.push('L3');
  }
  
  if (hasUndefined) {
    status.push('undefined values');
  }
  
  return `[${status.join('],[')}]`;
}

/**
 * Extract date ranges from array of dates
 * @param {Array<Date>} input - Array of dates
 * @returns {Array<Array<Date>>} Array of date range pairs [start, end]
 */
function extractDateRangesFromDateArray(input) {
  let ranges = [];
  let start;
  let last;
  
  for (const currentDate of input) {
    if (!start) {
      start = currentDate;
      continue;
    }

    if (!last && (currentDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) > 1
        || last && (currentDate.getTime() - last.getTime()) / (1000 * 60 * 60 * 24) > 1) {
      if (last) {
        ranges.push([start, last]);
      } else {
        ranges.push([start, start]);
      }
      start = currentDate;
      last = null;
      continue;
    }

    last = currentDate;
  }

  if (last) {
    ranges.push([start, last]);
  } else if (start) {
    ranges.push([start, start]);
  }

  return ranges;
}

/**
 * Format missing date ranges
 * @param {Array} missingRanges - Array of [start, end] date pairs
 * @returns {string} Formatted date ranges
 */
function formatMissingDateRanges(missingRanges) {
  if (!missingRanges || missingRanges.length === 0) return '';
  
  return missingRanges.map(range => {
    const [start, end] = range;
    if (start.getTime() === end.getTime()) {
      return `--- ${formatDate(start)}`;
    } else {
      return `--- ${formatDate(start)} bis ${formatDate(end)}`;
    }
  }).join('\n');
}

/**
 * Community detail formatters - modular approach
 */
const COMMUNITY_DETAIL_FORMATTERS = {
  /**
   * Format completeness analysis section
   * @param {Object} community - Community data
   * @returns {string} Formatted completeness section
   */
  completeness: (community) => {
    let details = `
  ==================================================
  ${community.name} (${community.ecn})
  ==================================================

Vollständigkeit: ${community.completenessPercentage || 0}% (${community.completeDays || 0}/${community.expectedDays || 0} Tage komplett)`;

    if (community.error) {
      details += `\n\nFEHLER: ${community.error}`;
      return details;
    }

    // Data range info
    if (community.firstDayWithData && community.lastDayWithData) {
      details += `\nErster Tag mit Daten: ${formatDate(community.firstDayWithData)}`;
      details += `\nLetzter Tag mit Daten: ${formatDate(community.lastDayWithData)}`;
    }

    // Missing days info
    if (community.daysMissingBefore !== undefined || community.daysMissingAfter !== undefined) {
      details += `\nFehlende Tage zu Beginn (gesamte Gemeinschaft): ${community.daysMissingBefore || 0}`;
      details += `\nFehlende Tage am Ende (gesamte Gemeinschaft): ${community.daysMissingAfter || 0}`;
    }

    // Day-by-day analysis
    if (community.completeDays !== undefined && community.incompleteDays !== undefined) {
      details += `\n\nTage komplett (innerhalb Gemeinschaft): ${community.completeDays}`;
      details += `\nTage mit fehlenden Daten (innerhalb Gemeinschaft): ${community.incompleteDays}`;
    }

    // Metering point analysis
    if (community.totalMeteringPoints !== undefined) {
      details += `\n\nZählpunkte im Betrachtungszeitraum`;
      details += `\nIn Summe: ${community.totalMeteringPoints} Zählpunkte`;
      
      if (community.memberDateDataDates) {
        const meteringPoints = Object.keys(community.memberDateDataDates);
        details += `\n${formatList(meteringPoints)}`;
      }
      
      details += `\n\nZählpunkte mit fehlenden Daten`;
      if (community.meteringPointsWithMissingData && community.meteringPointsWithMissingData.length > 0) {
        details += `\nIn Summe: ${community.meteringPointsWithMissingDataCount} Zählpunkte`;
        for (const mp of community.meteringPointsWithMissingData) {
          const direction = mp.meteringPointDirection === 'GENERATION' ? 'Einspeisung' : 'Bezug';
          details += `\n-- ${mp.meteringPoint} (${direction})`;
          
          if (mp.missingRanges && mp.missingRanges.length > 0) {
            details += `\n${formatMissingDateRanges(mp.missingRanges)}`;
          }
        }
      } else {
        details += `\nIn Summe: 0 Zählpunkte`;
        details += `\n-- Keine Zählpunkte mit fehlenden Daten gefunden.`;
      }
    }

    return details;
  },

  /**
   * Format sanity analysis section
   * @param {Object} community - Community data
   * @returns {string} Formatted sanity section
   */
  sanity: (community) => {
    let details = `
  ==================================================
  ${community.name} (${community.ecn})
  ==================================================

`;

    if (community.error) {
      details += `FEHLER: ${community.error}\n`;
      return details;
    }

    if (community.warningDueToBeforeOct2022) {
      details += `Warnung: Auswertung Energiesummen vor Oktober 2022 nicht aussagekräftig.\n\n`;
    }

    const intOptions6 = {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
      useGrouping: false
    };

    // Energy sums
    details += `Gesamtenergie von Mitgliedern: ${community.totalSumIn.toLocaleString(community.localeToUse, intOptions6)}kWh\n`;
    details += `Gesamtenergie an Mitglieder: ${community.totalSumOut.toLocaleString(community.localeToUse, intOptions6)}kWh\n\n`;

    // Data quality analysis
    const qualityCategories = [
      { key: 'L1', label: 'gemessenen Werten' },
      { key: 'L2', label: 'zumindest einem Ersatzwert', suffix: ', "belastbar"' },
      { key: 'L3', label: 'zumindest einem Ersatzwert', suffix: ', "nicht belastbar"' },
      { key: 'NotL1', label: 'zumindest unbekannter Klassifikation (bitte Support kontaktieren)' }
    ];

    qualityCategories.forEach(category => {
      const countKey = `count${category.key}QuarterHours`;
      const deviationKey = `totalDeviation${category.key}`;
      const absoluteDeviationKey = `totalAbsoluteDeviation${category.key}`;
      
      if (community[countKey] > 0) {
        details += `Viertelstunden mit ${category.label}${category.suffix || ''}: ${community[countKey]}\n`;
        details += `Summe aller Abweichungen (ein weniger aus): ${community[deviationKey].toLocaleString(community.localeToUse, intOptions6)}kWh\n`;
        details += `Summe aller Abweichungen (Absolutwerte): ${community[absoluteDeviationKey].toLocaleString(community.localeToUse, intOptions6)}kWh\n\n`;
      }
    });

    // Days with specific categories
    ['L2', 'L3', 'NotL1'].forEach(category => {
      const daysKey = `daysWith${category}`;
      if (community[daysKey] && community[daysKey].length > 0) {
        const categoryLabel = category === 'L2' ? 'L2-Werten' : 
                             category === 'L3' ? 'L3-Werten' : 
                             'unbekannter Klassifikation';
        details += `Tage mit ${categoryLabel}:\n`;
        const ranges = extractDateRangesFromDateArray(community[daysKey]);
        ranges.forEach(([start, end]) => {
          if (start.getTime() === end.getTime()) {
            details += `-- ${formatDate(start)}\n`;
          } else {
            details += `-- ${formatDate(start)} bis ${formatDate(end)}\n`;
          }
        });
        details += '\n';
      }
    });

    // Days with deviations
    if (community.daysWithNotZero && community.daysWithNotZero.length > 0) {
      details += `Tage mit absoluter Summe pro Viertelstunde > ${community.thresholdSumNotZero.toLocaleString(community.localeToUse, intOptions6)}kWh:\n`;
      const ranges = extractDateRangesFromDateArray(community.daysWithNotZero);
      ranges.forEach(([start, end]) => {
        if (start.getTime() === end.getTime()) {
          details += `-- ${formatDate(start)}\n`;
        } else {
          details += `-- ${formatDate(start)} bis ${formatDate(end)}\n`;
        }
      });
      details += '\n';
      details += `Limit von ${community.thresholdSumNotZero.toLocaleString(community.localeToUse, intOptions6)}kWh aufgrund Rundungsfehler im Schnellcheck\n`;
    }

    return details;
  },

  /**
   * Format combined analysis section
   * @param {Object} completenessData - Completeness analysis data
   * @param {Object} sanityData - Sanity analysis data
   * @returns {string} Formatted combined section
   */
  combined: (completenessData, sanityData = {}) => {
    let details = `
  ====================================================================
  ${completenessData.name} (${completenessData.ecn})
  ====================================================================

`;

    if (completenessData.error || sanityData.error) {
      details += `FEHLER: ${completenessData.error || sanityData.error}\n`;
      return details;
    }

    // Add completeness analysis
    details += `## VOLLSTÄNDIGKEITSANALYSE\n\n`;
    details += COMMUNITY_DETAIL_FORMATTERS.completeness(completenessData)
      .replace(/\n  ==================================================.*?==================================================\n\n/, '');

    // Add sanity analysis if available
    if (sanityData.hasData) {
      details += `\n\n## ENERGIEBILANZANALYSE\n`;
      details += COMMUNITY_DETAIL_FORMATTERS.sanity(sanityData)
        .replace(/\n  ==================================================.*?==================================================\n\n/, '');
    }

    return details;
  }
};

/**
 * Generic report formatter
 * @param {string} reportType - Type of report (completeness, sanity, combined)
 * @param {Object} analysisResults - Analysis results
 * @param {Array} selectedCommunities - Selected communities
 * @param {Object} dateRange - Date range {start, end}
 * @param {string|null} selectedBillingCycle - Billing cycle
 * @param {Function} getBillingCycleText - Function to get cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @param {Object} [sanityResults] - Optional sanity results for combined reports
 * @returns {string} Formatted report
 */
function _formatReport(
  reportType,
  analysisResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange,
  sanityResults = null
) {
  const config = REPORT_CONFIGS[reportType];
  const statusEvaluator = STATUS_EVALUATORS[reportType];
  const detailFormatter = COMMUNITY_DETAIL_FORMATTERS[reportType];
  
  // Build summary data
  const summaryData = { ...analysisResults.summary };
  if (sanityResults) {
    Object.assign(summaryData, sanityResults.summary);
  }

  const data = {
    reportTitle: config.title,
    reportTitleUnderline: config.titleUnderline,
    queryStart: formatDate(dateRange.start),
    queryEnd: formatDate(dateRange.end),
    analysisDate: formatDate(new Date()),
    billingCycleInfo: selectedBillingCycle 
      ? `\nAbrechnungsperiode: ${getBillingCycleText(selectedBillingCycle)}`
      : '',
    communityList: selectedCommunities.map(c => c.name).join(', '),
    summarySection: generateSummarySection(config, summaryData),
    communityStatusList: generateCommunityStatusList(
      analysisResults.communities, 
      statusEvaluator, 
      sanityResults?.communities
    ),
    communityDetails: reportType === 'combined' 
      ? analysisResults.communities.map(completenessData => {
          const sanityData = sanityResults.communities.find(s => s.ecn === completenessData.ecn) || {};
          return detailFormatter(completenessData, sanityData);
        }).join('\n\n')
      : analysisResults.communities.map(detailFormatter).join('\n\n')
  };

  return replacePlaceholders(REPORT_HEADER_TEMPLATE, data);
}

/**
 * Format completeness analysis report
 * @param {Object} analysisResults - Results from performCompletenessAnalysis
 * @param {Array} selectedCommunities - Selected community objects
 * @param {Object} dateRange - Date range {start, end}
 * @param {string|null} selectedBillingCycle - Billing cycle or null
 * @param {Function} getBillingCycleText - Function to get cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @returns {string} Formatted report
 */
export function formatCompletenessReport(
  analysisResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange
) {
  return _formatReport(
    'completeness',
    analysisResults,
    selectedCommunities,
    dateRange,
    selectedBillingCycle,
    getBillingCycleText,
    formatDateRange
  );
}

/**
 * Format sanity analysis report
 * @param {Object} analysisResults - Results from performSanityAnalysis
 * @param {Array} selectedCommunities - Selected community objects
 * @param {Object} dateRange - Date range {start, end}
 * @param {string|null} selectedBillingCycle - Billing cycle or null
 * @param {Function} getBillingCycleText - Function to get cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @returns {string} Formatted sanity report
 */
export function formatSanityReport(
  analysisResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange
) {
  return _formatReport(
    'sanity',
    analysisResults,
    selectedCommunities,
    dateRange,
    selectedBillingCycle,
    getBillingCycleText,
    formatDateRange
  );
}

/**
 * Format combined analysis report
 * @param {Object} completenessResults - Results from performCompletenessAnalysis
 * @param {Object} sanityResults - Results from performSanityAnalysis
 * @param {Array} selectedCommunities - Selected community objects
 * @param {Object} dateRange - Date range {start, end}
 * @param {string|null} selectedBillingCycle - Billing cycle or null
 * @param {Function} getBillingCycleText - Function to get cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @param {Object} dynamicDistributionResults - Results from performDynamicDistributionAnalysis
 * @returns {string} Formatted combined report
 */
export function formatCombinedReport(
  completenessResults,
  sanityResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange,
  dynamicDistributionResults = null
) {
  return formatCombinedReportDetailed(
    completenessResults,
    sanityResults,
    dynamicDistributionResults,
    selectedCommunities,
    dateRange,
    selectedBillingCycle,
    getBillingCycleText,
    formatDateRange
  );
}

/**
 * Format detailed combined analysis report matching the specific template format
 * @param {Object} completenessResults - Results from performCompletenessAnalysis
 * @param {Object} sanityResults - Results from performSanityAnalysis
 * @param {Object} dynamicDistributionResults - Results from performDynamicDistributionAnalysis
 * @param {Array} selectedCommunities - Selected community objects
 * @param {Object} dateRange - Date range {start, end}
 * @param {string|null} selectedBillingCycle - Billing cycle or null
 * @param {Function} getBillingCycleText - Function to get cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @returns {string} Formatted detailed combined report
 */
export function formatCombinedReportDetailed(
  completenessResults,
  sanityResults,
  dynamicDistributionResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange
) {
  let report = '';
  
  // Helper function to format a single community report
  const formatSingleCommunityReport = (community, completenessData, sanityData, dynamicData, isFirst = true) => {
    let communityReport = '';
    
    if (isFirst) {
      // Header section - Abfrage (only for first community)
      communityReport += `Abfrage\n`;
      communityReport += `\tGemeinschaft(en): ${selectedCommunities.map(c => c.ecn).join(', ')}\n`;
      communityReport += `\tAbfrage Start: ${formatDate(dateRange.start)}\n`;
      communityReport += `\tAbfrage Ende: ${formatDate(dateRange.end)}\n`;
      communityReport += `\tDatum Analyse: ${formatDate(new Date())}\n\n`;
      
      // Add Gemeinschaftsstatus section for all selected communities
      communityReport += `Gemeinschaftsstatus\n`;
      selectedCommunities.forEach(selectedCommunity => {
        const selectedSanityData = sanityResults.communities.find(c => c.ecn === selectedCommunity.ecn);
        const status = getDataQualityStatus(selectedSanityData);
        communityReport += `\t${selectedCommunity.ecn} - ${selectedCommunity.name} - ${status}\n`;
      });
      communityReport += `\n`;
    }
    
    // Always show community header for each report
    if (!isFirst) {
      communityReport += `\n`;
    }
    communityReport += `${'='.repeat(80)}\n`;
    communityReport += `${community.ecn} - ${community.name}\n`;
    communityReport += `${'='.repeat(80)}\n\n`;
    
    // Ergebnis Vollständigkeit
    if (completenessData && !completenessData.error) {
      communityReport += `Ergebnis Vollständigkeit\n`;
      if (completenessData.firstDayWithData && completenessData.lastDayWithData) {
        communityReport += `\tErster Tag mit Daten: ${formatDate(completenessData.firstDayWithData)}\n`;
        communityReport += `\tLetzter Tag mit Daten: ${formatDate(completenessData.lastDayWithData)}\n`;
      }
      communityReport += `\tFehlende Tage zu Beginn (gesamte Gemeinschaft): ${completenessData.daysMissingBefore || 0}\n`;
      communityReport += `\tFehlende Tage am Ende (gesamte Gemeinschaft): ${completenessData.daysMissingAfter || 0}\n\n`;
      
      communityReport += `\tTage komplett (innerhalb Gemeinschaft): ${completenessData.completeDays || 0}\n`;
      communityReport += `\tTage mit fehlenden Daten (innerhalb Gemeinschaft): ${completenessData.incompleteDays || 0}\n\t\n`;
      
      // Zählpunkte im Betrachtungszeitraum
      communityReport += `Zählpunkte im Betrachtungszeitraum \n`;
      communityReport += `\tIn Summe ${completenessData.totalMeteringPoints || 0} Zählpunkte\n`;
      if (completenessData.memberDateDataDates) {
        const meteringPoints = Object.keys(completenessData.memberDateDataDates);
        meteringPoints.forEach(mp => {
          communityReport += `\t-- ${mp}\n`;
        });
      }
      communityReport += '\n';
      
      // Zählpunkte mit fehlenden Daten
      communityReport += `Zählpunkte mit fehlenden Daten \n`;
      if (completenessData.meteringPointsWithMissingData && completenessData.meteringPointsWithMissingData.length > 0) {
        communityReport += `\tIn Summe ${completenessData.meteringPointsWithMissingDataCount} Zählpunkte\n`;
        completenessData.meteringPointsWithMissingData.forEach(mp => {
          communityReport += `\t-- ${mp.meteringPoint}\n`;
          if (mp.missingRanges && mp.missingRanges.length > 0) {
            mp.missingRanges.forEach(range => {
              if (range[0].getTime() === range[1].getTime()) {
                communityReport += `\t--- ${formatDate(range[0])}\n`;
              } else {
                communityReport += `\t--- ${formatDate(range[0])} bis ${formatDate(range[1])}\n`;
              }
            });
          }
        });
      } else {
        communityReport += `\tIn Summe 0 Zählpunkte\n`;
      }
      communityReport += '\n';
    }
    
    // Quickcheck Summen (from sanity data)
    if (sanityData && !sanityData.error) {
      const intOptions6 = {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
        useGrouping: false
      };
      
      communityReport += `Quickcheck Summen\n`;
      communityReport += `\tGesamtenergie von Mitgliedern: ${sanityData.totalSumIn.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n`;
      communityReport += `\tGesamtenergie an Mitglieder: ${sanityData.totalSumOut.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n\n`;
      
      // Quality categories
      if (sanityData.countL1QuarterHours > 0) {
        communityReport += `\tSumme Viertelstunden mit (keinem oder) gemessenen Werten (L1): ${sanityData.countL1QuarterHours}\n`;
        communityReport += `\tSumme aller Abweichungen (ein weniger aus): ${sanityData.totalDeviationL1.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n`;
        communityReport += `\tSumme aller Abweichungen (Absolutwerte): ${sanityData.totalAbsoluteDeviationL1.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n\n`;
      }
      
      if (sanityData.countL2QuarterHours > 0) {
        communityReport += `\tSumme Viertelstunden mit zumindest einem Ersatzwert (L2, "belastbar"): ${sanityData.countL2QuarterHours}\n`;
        communityReport += `\tSumme aller Abweichungen (ein weniger aus): ${sanityData.totalDeviationL2.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n`;
        communityReport += `\tSumme aller Abweichungen (Absolutwerte): ${sanityData.totalAbsoluteDeviationL2.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n\n`;
      }
      
      if (sanityData.countL3QuarterHours > 0) {
        communityReport += `\tSumme Viertelstunden mit zumindest einem Ersatzwert (L3, "nicht belastbar"): ${sanityData.countL3QuarterHours}\n`;
        communityReport += `\tSumme aller Abweichungen (ein weniger aus): ${sanityData.totalDeviationL3.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n`;
        communityReport += `\tSumme aller Abweichungen (Absolutwerte): ${sanityData.totalAbsoluteDeviationL3.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n\n`;
      }
      
      if (sanityData.countNotL1QuarterHours > 0) {
        communityReport += `\tSumme Viertelstunden mit zumindest unbekannter Klassifikation (bitte Support kontaktieren): ${sanityData.countNotL1QuarterHours}\n`;
        communityReport += `\tSumme aller Abweichungen (ein weniger aus): ${sanityData.totalDeviationNotL1.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n`;
        communityReport += `\tSumme aller Abweichungen (Absolutwerte): ${sanityData.totalAbsoluteDeviationNotL1.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh\n\t\n`;
      }
      
      // Days with specific quality categories
      ['L2', 'L3', 'NotL1'].forEach(category => {
        const daysKey = `daysWith${category}`;
        if (sanityData[daysKey] && sanityData[daysKey].length > 0) {
          const categoryLabel = category === 'L2' ? 'L2-Werten' : 
                               category === 'L3' ? 'L3-Werten' : 
                               'unbekannter Klassifikation';
          communityReport += `Tage mit ${categoryLabel}: \n`;
          const ranges = extractDateRangesFromDateArray(sanityData[daysKey]);
          ranges.forEach(([start, end]) => {
            if (start.getTime() === end.getTime()) {
              communityReport += `\t-- ${formatDate(start)}\n`;
            } else {
              communityReport += `\t-- ${formatDate(start)} bis ${formatDate(end)}\n`;
            }
          });
          communityReport += '\n';
        }
      });
      
      // Days with deviations
      if (sanityData.daysWithNotZero && sanityData.daysWithNotZero.length > 0) {
        communityReport += `Tage mit absoluter Summe pro Viertelstunde > ${sanityData.thresholdSumNotZero.toLocaleString(sanityData.localeToUse || 'de-AT', intOptions6)}kWh: \n`;
        const ranges = extractDateRangesFromDateArray(sanityData.daysWithNotZero);
        ranges.forEach(([start, end]) => {
          if (start.getTime() === end.getTime()) {
            communityReport += `\t-- ${formatDate(start)}\n`;
          } else {
            communityReport += `\t-- ${formatDate(start)} bis ${formatDate(end)}\n`;
          }
        });
        communityReport += '\n';
      }
    }
    
    // Dynamic Distribution section
    if (dynamicData && !dynamicData.error && dynamicData.hasData) {
      communityReport += `Dynamische Verteilung\n`;
      communityReport += `\tVersion: 0.4 (Vue3 Integration)\n`;
      communityReport += `\t\n`;
      communityReport += `\tErsatzwerte für Verteilung nicht berücksichtigt.\n`;
      communityReport += `\tMessdaten werden wie vorhanden verwendet.\n`;
      communityReport += `\t\n`;
      communityReport += `\tAuswertung der Abweichung.\n`;
      communityReport += `\tPrüfung auf leere (0kWh) Zuteilung - täglich.\n`;
      communityReport += `\tPrüfung auf leere (0kWh) Messung - täglich.\n`;
      communityReport += `\tAuflistung Zählpunkte mit L3-Werten.\n`;
      communityReport += `\t\n`;
      
      const distributionResult = dynamicData.distributionResult;
      if (distributionResult) {
        const formatNumber = (number, decimals = 6) => {
          const options = {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: false
          };
          return number.toLocaleString('de-AT', options);
        };
        
        const formatPercentage = (number) => {
          const options = {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false
          };
          return number.toLocaleString('de-AT', options);
        };
        
        // Overall sums
        communityReport += `Aufstellung Summen (Gesamt)\n`;
        if (distributionResult.totalSums) {
          const sums = distributionResult.totalSums;
          
          // Helper function to safely calculate percentage
          const safePercentage = (numerator, denominator) => {
            if (denominator === 0) return formatPercentage(0);
            return formatPercentage(numerator / denominator * 100);
          };
          
          communityReport += `\tGesamter Überschuss: ${formatNumber(sums.totalProduction)}kWh\n`;
          communityReport += `\tGemeinschaftsanteil Überschuss (VNB): ${formatNumber(sums.totalAssignmentProducerDso)}kWh\n`;
          communityReport += `\tGemeinschaftsanteil Überschuss (Referenzrechnung): ${formatNumber(sums.totalAssignmentProducerReferenceCalc)}kWh\n`;
          communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Produzenten (arithmetisch): ${formatNumber(sums.diffSumProductionAssigned)}kWh (${safePercentage(sums.diffSumProductionAssigned, sums.totalProduction)}%)\n`;
          communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Produzenten (Absolutwerte summiert): ${formatNumber(sums.diffSumProductionAssignedAbs)}kWh (${safePercentage(sums.diffSumProductionAssignedAbs, sums.totalProduction)}%)\n`;
          communityReport += `\n`;
          
          communityReport += `\tGesamter Verbrauch: ${formatNumber(sums.totalConsumption)}kWh\n`;
          communityReport += `\tGemeinschaftsanteil Verbrauch (VNB): ${formatNumber(sums.totalAssignmentConsumptionDso)}kWh\n`;
          communityReport += `\tGemeinschaftsanteil Verbrauch (Referenzrechnung): ${formatNumber(sums.totalAssignmentConsumptionReferenceCalc)}kWh\n`;
          communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Verbrauchern (arithmetisch): ${formatNumber(sums.diffSumConsumptionAssigned)}kWh (${safePercentage(sums.diffSumConsumptionAssigned, sums.totalConsumption)}%)\n`;
          communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Verbrauchern (Absolutwerte summiert): ${formatNumber(sums.diffSumConsumptionAssignedAbs)}kWh (${safePercentage(sums.diffSumConsumptionAssignedAbs, sums.totalConsumption)}%)\n`;
          communityReport += `\n`;
        }
        
        // DSO-specific sums if available (only show when there are multiple DSOs)
        if (distributionResult.byDso && distributionResult.byDso.length >= 2) {
          for (const dsoEntry of distributionResult.byDso) {
            communityReport += `Aufstellung Summen (${dsoEntry.ecn})\n`;
            if (dsoEntry.totalSums) {
              const sums = dsoEntry.totalSums;
              
              // Helper function to safely calculate percentage
              const safePercentage = (numerator, denominator) => {
                if (denominator === 0) return formatPercentage(0);
                return formatPercentage(numerator / denominator * 100);
              };
              
              communityReport += `\tGesamter Überschuss: ${formatNumber(sums.totalProduction)}kWh\n`;
              communityReport += `\tGemeinschaftsanteil Überschuss (VNB): ${formatNumber(sums.totalAssignmentProducerDso)}kWh\n`;
              communityReport += `\tGemeinschaftsanteil Überschuss (Referenzrechnung): ${formatNumber(sums.totalAssignmentProducerReferenceCalc)}kWh\n`;
              communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Produzenten (arithmetisch): ${formatNumber(sums.diffSumProductionAssigned)}kWh (${safePercentage(sums.diffSumProductionAssigned, sums.totalProduction)}%)\n`;
              communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Produzenten (Absolutwerte summiert): ${formatNumber(sums.diffSumProductionAssignedAbs)}kWh (${safePercentage(sums.diffSumProductionAssignedAbs, sums.totalProduction)}%)\n\n`;
              
              communityReport += `\tGesamter Verbrauch: ${formatNumber(sums.totalConsumption)}kWh\n`;
              communityReport += `\tGemeinschaftsanteil Verbrauch (VNB): ${formatNumber(sums.totalAssignmentConsumptionDso)}kWh\n`;
              communityReport += `\tGemeinschaftsanteil Verbrauch (Referenzrechnung): ${formatNumber(sums.totalAssignmentConsumptionReferenceCalc)}kWh\n`;
              communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Verbrauchern (arithmetisch): ${formatNumber(sums.diffSumConsumptionAssigned)}kWh (${safePercentage(sums.diffSumConsumptionAssigned, sums.totalConsumption)}%)\n`;
              communityReport += `\tUnterschied Verteilung (VNB-Referenz) bei Verbrauchern (Absolutwerte summiert): ${formatNumber(sums.diffSumConsumptionAssignedAbs)}kWh (${safePercentage(sums.diffSumConsumptionAssignedAbs, sums.totalConsumption)}%)\n`;
              communityReport += `\t\n`;
            }
          }
        }
        
        // Check for problems and display accordingly (moved before L3 analysis)
        const missingDist = distributionResult.missingDistribution;
        if (missingDist && (missingDist.communityProblematicDates.length > 0 || missingDist.mpsProblematicDates.length > 0)) {
          // We have problems - show detailed problem sections
          communityReport += `\t\n`;
          communityReport += `Gemeinschaft - Zeiträume mit Problemen:\n`;
          
          if (missingDist.communityProblematicDates.length > 0) {
            const ranges = extractDateRangesFromDateArray(missingDist.communityProblematicDates);
            ranges.forEach(([start, end]) => {
              if (start.getTime() === end.getTime()) {
                communityReport += `\t-- ${formatDate(start)}\n`;
              } else {
                communityReport += `\t-- ${formatDate(start)} bis ${formatDate(end)}\n`;
              }
            });
          }
          communityReport += `\n`;
          
          if (missingDist.mpsProblematicDates.length > 0) {
            communityReport += `Einzelne Zählpunkte:\n`;
            for (const mpmEntry of missingDist.mpsProblematicDates) {
              const direction = mpmEntry.membership?.meteringPointDirection === 'GENERATION' ? 'Einspeisung' : 'Bezug';
              communityReport += `\t- ${mpmEntry.membership?.meteringPoint} (${direction})\n`;
              
              for (const problem of mpmEntry.problems) {
                if (problem.type === "NO_ASSIGNMENT") {
                  communityReport += `\t-- ${formatDate(problem.date)}: keine Zuteilung\n`;
                } else {
                  communityReport += `\t-- ${formatDate(problem.date)}: ${problem.type}\n`;
                }
              }
              communityReport += `\n`;
            }
          }
        } else {
          communityReport += `\tKeine Auffälligkeiten im Zeitraum bei der Verteilung gefunden.\n`;
        }
        
        // List all metering points with L3 values (after problems analysis)
        const communityEnergyData = distributionResult.communityEnergyData || [];
        let l3Report = '';
        
        for (const mpmEntry of communityEnergyData) {
          if ((mpmEntry.datesTotalL3 && mpmEntry.datesTotalL3.length === 0) && 
              (mpmEntry.datesCommunityL3 && mpmEntry.datesCommunityL3.length === 0)) {
            continue;
          }
          
          const direction = mpmEntry.membership?.meteringPointDirection === 'GENERATION' ? 'Einspeisung' : 'Bezug';
          l3Report += `\tL3-Wert(e) für Zählpunkt ${mpmEntry.membership?.meteringPoint} (${direction}):\n`;
          
          if (mpmEntry.datesTotalL3 && mpmEntry.datesTotalL3.length > 0) {
            l3Report += `\t\t- Messung\n`;
            const ranges = extractDateRangesFromDateArray(mpmEntry.datesTotalL3);
            ranges.forEach(([start, end]) => {
              if (start.getTime() === end.getTime()) {
                l3Report += `\t\t\t-- ${formatDate(start)}\n`;
              } else {
                l3Report += `\t\t\t-- ${formatDate(start)} - ${formatDate(end)}\n`;
              }
            });
          }
          
          if (mpmEntry.datesCommunityL3 && mpmEntry.datesCommunityL3.length > 0) {
            l3Report += `\t\t- Zuteilung\n`;
            const ranges = extractDateRangesFromDateArray(mpmEntry.datesCommunityL3);
            ranges.forEach(([start, end]) => {
              if (start.getTime() === end.getTime()) {
                l3Report += `\t\t\t-- ${formatDate(start)}\n`;
              } else {
                l3Report += `\t\t\t-- ${formatDate(start)} - ${formatDate(end)}\n`;
              }
            });
          }
          l3Report += `\n`;
        }
        
        if (l3Report.length > 0) {
          communityReport += `Analyse Vorhandensein L3-Werte pro Zählpunkt:\n`;
          communityReport += l3Report;
        } else {
          communityReport += `\tKeine L3-Werte Tage vorhanden\n`;
        }
        communityReport += `\t\n`;
        
        // Zero profile analysis
        let zeroReport = '';
        for (const mpmEntry of communityEnergyData) {
          if ((mpmEntry.datesTotalZeros && mpmEntry.datesTotalZeros.length === 0) && 
              (mpmEntry.datesCommunityZeros && mpmEntry.datesCommunityZeros.length === 0)) {
            continue;
          }
          
          const direction = mpmEntry.membership?.meteringPointDirection === 'GENERATION' ? 'Einspeisung' : 'Bezug';
          zeroReport += `\tNullprofil für Zählpunkt ${mpmEntry.membership?.meteringPoint} (${direction}):\n`;
          
          if (mpmEntry.datesTotalZeros && mpmEntry.datesTotalZeros.length > 0) {
            zeroReport += `\t\t- Messung \n`;
            const ranges = extractDateRangesFromDateArray(mpmEntry.datesTotalZeros);
            ranges.forEach(([start, end]) => {
              if (start.getTime() === end.getTime()) {
                zeroReport += `\t\t\t-- ${formatDate(start)}\n`;
              } else {
                zeroReport += `\t\t\t-- ${formatDate(start)} - ${formatDate(end)}\n`;
              }
            });
          }
          
          if (mpmEntry.datesCommunityZeros && mpmEntry.datesCommunityZeros.length > 0) {
            zeroReport += `\t\t- Zuteilung \n`;
            const ranges = extractDateRangesFromDateArray(mpmEntry.datesCommunityZeros);
            ranges.forEach(([start, end]) => {
              if (start.getTime() === end.getTime()) {
                zeroReport += `\t\t\t-- ${formatDate(start)}\n`;
              } else {
                zeroReport += `\t\t\t-- ${formatDate(start)} - ${formatDate(end)}\n`;
              }
            });
          }
        }
        
        if (zeroReport.length > 0) {
          communityReport += `Analyse Null-Profiltage pro Zählpunkt\n`;
          communityReport += zeroReport;
        }
      }
    }
    
    return communityReport;
  };
  
  // Process each community
  selectedCommunities.forEach((community, index) => {
    const completenessData = completenessResults.communities.find(c => c.ecn === community.ecn);
    const sanityData = sanityResults.communities.find(c => c.ecn === community.ecn);
    const dynamicData = dynamicDistributionResults?.communities.find(c => c.ecn === community.ecn);
    
    report += formatSingleCommunityReport(community, completenessData, sanityData, dynamicData, index === 0);
  });
  
  return report;
}

/**
 * Format error report
 * @param {Error} error - Error object
 * @returns {string} Formatted error report
 */
export function formatErrorReport(error) {
  const errorMessage = error?.message || error?.toString() || 'Unbekannter Fehler aufgetreten';
  
  return replacePlaceholders(ERROR_TEMPLATE, {
    errorMessage: errorMessage
  });
}

/**
 * Format dynamic distribution analysis report
 * @param {Object} analysisResults - Dynamic distribution analysis results
 * @param {Array} selectedCommunities - Selected communities
 * @param {Object} dateRange - Date range object
 * @param {string} selectedBillingCycle - Selected billing cycle
 * @param {Function} getBillingCycleText - Function to get billing cycle text
 * @param {Function} formatDateRange - Function to format date range
 * @returns {string} Formatted dynamic distribution report
 */
export async function formatDynamicDistributionReport(
  analysisResults,
  selectedCommunities,
  dateRange,
  selectedBillingCycle,
  getBillingCycleText,
  formatDateRange
) {
  // Import utility functions from the shared-js module using dynamic imports
  const { formatDate } = await import('@/lib/es-bill-shared-js/misc/timeTools.js');
  const isConsumptionDirectionModule = await import('@/lib/es-bill-shared-js/util/MeteringPointDirectionUtil.js');
  const isConsumptionDirection = isConsumptionDirectionModule.default;
  
  // Helper function to format number with 6 decimal places like in classic implementation
  const formatNumber = (number, decimals = 6) => {
    const options = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false
    };
    return number.toLocaleString('de-AT', options);
  };
  
  // Helper function to format number with 2 decimal places for percentages
  const formatPercentage = (number) => {
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false
    };
    return number.toLocaleString('de-AT', options);
  };
  
  // Helper function to format date ranges from date array like in classic implementation
  const formatDateRanges = (ranges) => {
    return ranges.map(entry => {
      if (entry[0].getDate() !== entry[1].getDate()) {
        return `${formatDate(entry[0])} bis ${formatDate(entry[1])}`;
      } else {
        return formatDate(entry[0]);
      }
    });
  };
  
  // Helper function to generate sum printout like getSumPrintOut in classic implementation
  const getSumPrintOut = (subject) => {
    let output = "";
    
    output += "Gesamter Überschuss: " + 
        formatNumber(subject.totalSums.totalProduction) + "kWh\n";
    output += "Gemeinschaftsanteil Überschuss (VNB): " + 
        formatNumber(subject.totalSums.totalAssignmentProducerDso) + "kWh\n";
    output += "Gemeinschaftsanteil Überschuss (Referenzrechnung): " + 
        formatNumber(subject.totalSums.totalAssignmentProducerReferenceCalc) + "kWh\n";
    
    output += "Unterschied Verteilung (VNB-Referenz) bei Produzenten (arithmetisch): " + 
        formatNumber(subject.totalSums.diffSumProductionAssigned) + "kWh (" + 
        formatPercentage(subject.totalSums.diffSumProductionAssigned / subject.totalSums.totalProduction * 100) + "%)\n";
    output += "Unterschied Verteilung (VNB-Referenz) bei Produzenten (Absolutwerte summiert): " + 
        formatNumber(subject.totalSums.diffSumProductionAssignedAbs) + "kWh (" + 
        formatPercentage(subject.totalSums.diffSumProductionAssignedAbs / subject.totalSums.totalProduction * 100) + "%)\n";
    output += "\n";
    
    output += "Gesamter Verbrauch: " + 
        formatNumber(subject.totalSums.totalConsumption) + "kWh\n";
    output += "Gemeinschaftsanteil Verbrauch (VNB): " + 
        formatNumber(subject.totalSums.totalAssignmentConsumptionDso) + "kWh\n";
    output += "Gemeinschaftsanteil Verbrauch (Referenzrechnung): " + 
        formatNumber(subject.totalSums.totalAssignmentConsumptionReferenceCalc) + "kWh\n";
    
    output += "Unterschied Verteilung (VNB-Referenz) bei Verbrauchern (arithmetisch): " + 
        formatNumber(subject.totalSums.diffSumConsumptionAssigned) + "kWh (" + 
        formatPercentage(subject.totalSums.diffSumConsumptionAssigned / subject.totalSums.totalConsumption * 100) + "%)\n";
    output += "Unterschied Verteilung (VNB-Referenz) bei Verbrauchern (Absolutwerte summiert): " + 
        formatNumber(subject.totalSums.diffSumConsumptionAssignedAbs) + "kWh (" + 
        formatPercentage(subject.totalSums.diffSumConsumptionAssignedAbs / subject.totalSums.totalConsumption * 100) + "%)\n";
    output += "\n";
    
    return output;
  };
  
  let report = `AUSWERTUNG DYNAMISCHE VERTEILUNG
=================================

Gemeinschaft: ${selectedCommunities.map(c => `${c.name} (${c.ecn})`).join(', ')}
Abfrage Start: ${formatDate(dateRange.start)}
Abfrage Ende: ${formatDate(dateRange.end)}
Datum Analyse: ${formatDate(new Date())}
Version: 0.4 (Vue3 Integration)
${selectedBillingCycle ? `Abrechnungsperiode: ${getBillingCycleText(selectedBillingCycle)}` : ''}

================================================================================

## ZUSAMMENFASSUNG

Gemeinschaften gesamt: ${analysisResults.summary.totalCommunities}
Gemeinschaften analysiert: ${analysisResults.summary.communitiesAnalyzed}
Gemeinschaften mit Fehlern: ${analysisResults.summary.communitiesWithErrors}
Gemeinschaften mit Problemen: ${analysisResults.summary.communitiesWithProblems}

================================================================================

## ANALYSE-HINWEISE

• Ersatzwerte für Verteilung nicht berücksichtigt
• Messdaten werden wie vorhanden verwendet
• Auswertung der Abweichungen
• Prüfung auf leere (0kWh) Zuteilung - täglich
• Prüfung auf leere (0kWh) Messung - täglich
• Auflistung Zählpunkte mit L3-Werten

================================================================================

## DETAILLIERTE ERGEBNISSE

`;
  
  // Process each community
  for (const community of analysisResults.communities) {
    if (selectedCommunities.length > 1) {
      report += `
  ==================================================
  ${community.name} (${community.ecn})
  ==================================================

`;
    }
    
    if (community.error) {
      report += `FEHLER: ${community.error}\n\n`;
      continue;
    }
    
    if (!community.hasData) {
      report += "Keine Daten für Analyse verfügbar\n\n";
      continue;
    }
    
    const distributionResult = community.distributionResult;
    
    // Overall sums using classic format
    report += "Aufstellung Summen (Gesamt)\n";
    report += "----------------------------\n";
    report += getSumPrintOut(distributionResult);
    
    // DSO-specific sums if available
    if (distributionResult.byDso && distributionResult.byDso.length >= 2) {
      for (const dsoEntry of distributionResult.byDso) {
        report += `Aufstellung Summen (${dsoEntry.ecn})\n`;
        report += "----------------------------\n";
        report += getSumPrintOut(dsoEntry);
      }
    }
    
    // Problem analysis using classic format
    const missingDist = distributionResult.missingDistribution;
    if (missingDist && (missingDist.communityProblematicDates.length > 0 || missingDist.mpsProblematicDates.length > 0)) {
      report += "Gemeinschaft - Zeiträume mit Problemen:\n";
      report += "---------------------------------------\n";
      
      if (missingDist.communityProblematicDates.length > 0) {
        const ranges = extractDateRangesFromDateArray(missingDist.communityProblematicDates);
        const formattedRanges = formatDateRanges(ranges);
        for (const range of formattedRanges) {
          report += `-- ${range}\n`;
        }
      }
      report += "\n";
      
      if (missingDist.mpsProblematicDates.length > 0) {
        report += "Einzelne Zählpunkte:\n";
        report += "--------------------\n";
        for (const mpmEntry of missingDist.mpsProblematicDates) {
          const direction = isConsumptionDirection(mpmEntry.membership.meteringPointDirection) ? 
            "Bezug" : "Einspeisung";
          report += `- ${mpmEntry.membership.meteringPoint} (${direction})\n`;
          
          for (const problem of mpmEntry.problems) {
            if (problem.type === "NO_ASSIGNMENT") {
              report += `  -- ${formatDate(problem.date)}: keine Zuteilung\n`;
            } else {
              report += `  -- ${formatDate(problem.date)}: ${problem.type}\n`;
            }
          }
          report += "\n";
        }
      }
    } else {
      report += "Keine Auffälligkeiten im Zeitraum bei der Verteilung gefunden.\n";
    }
    report += "\n";
    
    // L3 values analysis using classic format
    const communityEnergyData = distributionResult.communityEnergyData || [];
    let l3Report = "";
    
    for (const mpmEntry of communityEnergyData) {
      if ((mpmEntry.datesTotalL3 && mpmEntry.datesTotalL3.length === 0) && 
          (mpmEntry.datesCommunityL3 && mpmEntry.datesCommunityL3.length === 0)) {
        continue;
      }
      
      const direction = isConsumptionDirection(mpmEntry.membership.meteringPointDirection) ? 
        "Bezug" : "Einspeisung";
      l3Report += `L3-Wert(e) für Zählpunkt ${mpmEntry.membership.meteringPoint} (${direction}):\n`;
      
      if (mpmEntry.datesTotalL3 && mpmEntry.datesTotalL3.length > 0) {
        l3Report += "- Messung\n";
        const ranges = extractDateRangesFromDateArray(mpmEntry.datesTotalL3);
        const formattedRanges = formatDateRanges(ranges);
        for (const range of formattedRanges) {
          l3Report += `  -- ${range}\n`;
        }
      }
      
      if (mpmEntry.datesCommunityL3 && mpmEntry.datesCommunityL3.length > 0) {
        l3Report += "- Zuteilung\n";
        const ranges = extractDateRangesFromDateArray(mpmEntry.datesCommunityL3);
        const formattedRanges = formatDateRanges(ranges);
        for (const range of formattedRanges) {
          l3Report += `  -- ${range}\n`;
        }
      }
      l3Report += "\n";
    }
    
    if (l3Report.length > 0) {
      report += "Analyse Vorhandensein L3-Werte pro Zählpunkt:\n";
      report += "---------------------------------------------\n";
      report += l3Report;
    } else {
      report += "Keine L3-Werte Tage vorhanden\n";
    }
    report += "\n";
    
    // Zero profile analysis using classic format
    let zeroReport = "";
    for (const mpmEntry of communityEnergyData) {
      if ((mpmEntry.datesTotalZeros && mpmEntry.datesTotalZeros.length === 0) && 
          (mpmEntry.datesCommunityZeros && mpmEntry.datesCommunityZeros.length === 0)) {
        continue;
      }
      
      const direction = isConsumptionDirection(mpmEntry.membership.meteringPointDirection) ? 
        "Bezug" : "Einspeisung";
      zeroReport += `Nullprofil für Zählpunkt ${mpmEntry.membership.meteringPoint} (${direction}):\n`;
      
      if (mpmEntry.datesTotalZeros && mpmEntry.datesTotalZeros.length > 0) {
        zeroReport += "- Messung\n";
        const ranges = extractDateRangesFromDateArray(mpmEntry.datesTotalZeros);
        const formattedRanges = formatDateRanges(ranges);
        for (const range of formattedRanges) {
          zeroReport += `  -- ${range}\n`;
        }
      }
      
      if (mpmEntry.datesCommunityZeros && mpmEntry.datesCommunityZeros.length > 0) {
        zeroReport += "- Zuteilung\n";
        const ranges = extractDateRangesFromDateArray(mpmEntry.datesCommunityZeros);
        const formattedRanges = formatDateRanges(ranges);
        for (const range of formattedRanges) {
          zeroReport += `  -- ${range}\n`;
        }
      }
      zeroReport += "\n";
    }
    
    if (zeroReport.length > 0) {
      report += "Analyse Null-Profiltage pro Zählpunkt:\n";
      report += "--------------------------------------\n";
      report += zeroReport;
    } else {
      report += "Keine Null-Profiltage vorhanden\n";
    }
    report += "\n";
  }
  
  return report;
} 